import { create, tsx } from '@dojo/framework/core/vdom';
import { Link } from '@dojo/framework/routing/Link';
import { Head } from '../../widgets/Head';
import Hero from '../../widgets/Hero';
import Summary from '../../widgets/Summary';
import ConnectStrip from '../../widgets/ConnectStrip';
import FAQ from '../../widgets/FAQ';
import { IndustryExpertise } from '../../widgets/IndustryExpertise';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Text from '../../widgets/Text';
import ColumnSummary from '../../widgets/ColumnSummary';
import Image from '../../widgets/Image';
import SitePenLogo from '../../widgets/SitepenLogo';

const engineeringImg = require('../../assets/images/illustration/engineering.svg');
const softwareCycleImg = require('../../assets/images/illustration/software-cycle.svg');

const softwareImg = require('../../assets/images/icon-illustration/software-consulting-design.svg');
const customImg = require('../../assets/images/icon-illustration/custom-software.svg');
const crossPlatformImg = require('../../assets/images/icon-illustration/cross-platform-development.svg');
const legacyImg = require('../../assets/images/icon-illustration/legacy-software-modernization.svg');
const frontEndAppImg = require('../../assets/images/icon-illustration/front-end-web-app.svg');
const backEndServicesImg = require('../../assets/images/icon-illustration/back-end-services.svg');
const cloudImg = require('../../assets/images/icon-illustration/cloud-infrastructure.svg');
const databaseDevImg = require('../../assets/images/icon-illustration/database-development.svg');

const factory = create();

export const CustomSoftwareDevelopment = factory(function CustomSoftwareDevelopment() {
	return (
		<div>
			<Head
				title="Custom Software Development Services | SitePen"
				description="Custom app development company building enterprise solutions to improve processes, streamline operations, and empower business success."
			/>

			<Hero background="level3Purple">
				{{
					xlTitle: <div>App Development</div>
				}}
			</Hero>
			<Section type="leading">
				<ContentWrapper>
					<Summary reverse>
						{{
							title: 'Build for Innovation ',
							blurb: (
								<virtual>
									<p>
										In today's world, every enterprise is driven by software.
										Through a mix of off-the-shelf and custom applications, each
										organization builds a virtual world that supports their
										goals and objectives. In these worlds, pre-packaged
										applications provide a known, reliable foundation. These are
										vital components, but insufficient to address the unique
										challenges and goals of each enterprise. Custom software
										energizes this virtual world, granting it the ability to
										adapt as markets evolve and business needs change.
									</p>
									<p>
										SitePen offers a wide range of custom software services that
										are tailored to meet the different challenges that
										enterprises face. Whether entering new markets or expanding
										existing ones, new applications and services often need to
										be integrated into the current software ecosystem. Our
										design and consulting services are ideal for identifying and
										refining system requirements and developing robust
										person-machine interfaces. As applications age, they often
										become encumbered with outdated technologies, and their code
										base's structure begins to degrade. SitePen's engineering
										team has extensive experience in enterprise technologies,
										both past, and present. This knowledge allows them to
										develop clear designs that can breathe new life into legacy
										systems.
									</p>
								</virtual>
							),
							image: <Image src={engineeringImg} size="lg" width={400} height={350} />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<ColumnSummary>
						{{
							title: 'Custom Software Development Services',
							blurb: (
								<virtual>
									Custom software is the vehicle that supports the unique
									challenges of fulfilling your user's desire to obtain your
									products or services. We will partner with you to learn how your
									business works, and then we will work with you to transform
									those processes into efficient and maintainable software.
								</virtual>
							),
							items: [
								{
									image: <Image src={softwareImg} align="center" lazy />,
									title: 'Software Consulting & Design',
									blurb: (
										<virtual>
											Many enterprises struggle to mesh business needs with
											software engineering capabilities. Our extensive
											experience in a wide range of industries makes us a
											perfect partner when developing software strategies that
											provide high-value solutions. At the tactical level, our
											design team's expertise is an invaluable resource when
											designing new application interfaces that are efficient,
											error-resistant, and intuitive.
										</virtual>
									)
								},
								{
									image: <Image src={customImg} align="center" lazy />,
									title: 'End-to-end Software Development',
									blurb: (
										<virtual>
											SitePen's engineering teams monitor the software
											development community's ever-changing landscape. We
											bring this knowledge to all aspects of our projects,
											from the user-interface to the database and back. This
											understanding ensures that every part of the software
											solution reflects best-practices and is optimized, both
											as individual components and as a holistic system.
										</virtual>
									)
								},
								{
									image: <Image src={legacyImg} align="center" lazy />,
									title: 'Legacy Software Modernization',
									blurb: (
										<virtual>
											Nothing lasts forever, especially software developed in
											an ever-changing market. We understand the challenges
											and opportunities that legacy software modernization
											projects bring. We believe that most applications can
											have several years added to their useful lifespans
											through careful analysis and strategic updates that keep
											most of the application intact while supporting evolving
											business objectives.
										</virtual>
									)
								}
							]
						}}
					</ColumnSummary>
				</ContentWrapper>
			</Section>
			<Section>
				<IndustryExpertise />
			</Section>

			<Section shaded>
				<ContentWrapper>
					<ColumnSummary columnCount={5}>
						{{
							title: 'Technologies We Use',
							blurb: (
								<virtual>
									Enterprise applications are composed of a complex set of
									interconnected systems that must work smoothly together. At
									SitePen, we have expertise throughout the entire development
									stack, including front-end web applications, back-end web
									services and applications, cross-platform development,
									cloud-based infrastructure, and database development and
									optimization.
								</virtual>
							),
							items: [
								{
									title: (
										<Text type="subhead" align="center">
											Front-end Web
											<br />
											Applications
										</Text>
									),
									image: <Image src={frontEndAppImg} align="center" lazy />,
									blurb: (
										<Text type="body" align="center">
											<Link to="expertiseJavascript">JavaScript</Link>,{' '}
											<Link to="expertiseTypeScript">TypeScript</Link>,{' '}
											<Link to="expertiseAngular">Angular</Link>,{' '}
											<Link to="expertiseReact">React</Link>,{' '}
											<Link to="expertiseVue">Vue</Link>,{' '}
											<Link to="expertiseDojo">Dojo</Link>
										</Text>
									)
								},
								{
									title: (
										<Text type="subhead" align="center">
											Back-end
											<br />
											Web Services
										</Text>
									),
									image: <Image src={backEndServicesImg} align="center" lazy />,
									blurb: (
										<Text type="body" align="center">
											<Link to="expertiseNode">Node.js</Link>, Java, PHP
										</Text>
									)
								},
								{
									title: (
										<Text type="subhead" align="center">
											Multi-Platform
											<br />
											Apps
										</Text>
									),
									image: <Image src={crossPlatformImg} align="center" lazy />,
									blurb: (
										<Text type="body" align="center">
											<Link to="servicesCrossPlatformDevelopment">
												iOS, Android
											</Link>
										</Text>
									)
								},
								{
									title: (
										<Text type="subhead" align="center">
											Cloud-based
											<br />
											Infrastructure
										</Text>
									),
									image: <Image src={cloudImg} align="center" lazy />,
									blurb: (
										<Text type="body" align="center">
											<Link to="servicesEnterpriseCloudServices">
												AWS, Azure, Google Cloud
											</Link>
										</Text>
									)
								},
								{
									title: (
										<Text type="subhead" align="center">
											Database
											<br />
											Development
										</Text>
									),
									image: <Image src={databaseDevImg} align="center" lazy />,
									blurb: (
										<Text type="body" align="center">
											MySQL, MariaDB, MongoDB, RavenDB, PostgreSQL
										</Text>
									)
								}
							]
						}}
					</ColumnSummary>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper size="small">
					<Summary stack reverse paddingBottom="sm">
						{{
							title: 'Choosing the Right Software Development Company',
							blurb: (
								<virtual>
									There are many options to choose from when it comes to picking a
									software development company. Many of those companies focus on
									telling you what they do well. At SitePen, we believe that the
									ideal company for you to work with is one that listens first. We
									want to get to know you, your company, and your needs. As we
									learn more about you, we can offer the help and solutions that
									are most beneficial to you.
								</virtual>
							)
						}}
					</Summary>
					<Summary stack paddingBottom="sm">
						{{
							title: 'Why Develop with SitePen',
							image: <SitePenLogo />,
							blurb: (
								<virtual>
									<p>
										When SitePen was founded almost 20 years ago, it had three
										characteristics that have made it the ideal partner for many
										enterprises. Its founders believed that front-end internet
										applications were the future, its employees worked 100%
										remotely, and it was firmly committed to the open-source
										community.
									</p>
									<p>
										Today, it is assumed that any web application will contain a
										significant amount of client-side functionality. Twenty
										years ago, things were less clear. JavaScript support was
										inconsistent and slow and simply getting two web browsers to
										exhibit the same behavior was challenging. Despite these
										problems, SitePen understood the rich rewards that a
										successful front-end application could yield in terms of
										user satisfaction and efficiency. We continue to look at the
										latest technology trends, applying the best ideas to
										maximize your return-on-investment.
									</p>
									<p>
										In the early 2000s, almost every company had a physical
										office. Our founders realized that operating in a
										remote-only fashion promised dramatic advantages if it could
										be made to work. This decision has allowed SitePen to hire
										the most talented engineers, designers, and project managers
										from around the world. We have also developed robust
										communications channels to share information with clients as
										transparently as we do internally.
									</p>
									<p>
										Another characteristic that makes SitePen a valuable partner
										is its commitment to the open-source community. Our
										engineering teams continuously hone their skills by
										supporting the development of large projects, such as{' '}
										<a target="_blank" rel="noopener" href="https://dojo.io">
											Dojo
										</a>{' '}
										and{' '}
										<a
											target="_blank"
											rel="noopener"
											href="https://theintern.io"
										>
											The Intern
										</a>
										. Open-source projects also allow us to learn from other
										contributors, keeping our teams engaged with a broad
										cross-section of the software development community.
									</p>
								</virtual>
							)
						}}
					</Summary>
					<Summary stack>
						{{
							title: 'Our Enterprise Software Development Process',
							image: <Image src={softwareCycleImg} lazy />,
							blurb: (
								<virtual>
									Our development partnership with you starts the moment you reach
									out to us. Our solution architects will work with you to
									understand your goals and gather the background information that
									informs our entire development process. Our project management,
									senior designers, and technical leads will take this information
									and develop high-level project plans before you even see a
									contract. We will then work with you to ensure that we agree
									upon a set of clear, achievable goals. Throughout the
									development process, we stay in constant contact with you. As we
									all learn more about the project, we'll find areas that need to
									be refined and changed. We will work with you to integrate these
									changes into the plan so that the software reflects all of the
									lessons we learned during its development.
								</virtual>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="Custom Software Development FAQs">
					{[
						{
							title: 'What Is Custom Software Development?',
							content: (
								<p>
									Custom Software Development is the practice of developing
									software systems that are unique to a business or enterprise.
									They are usually designed to automate business processes to
									increase the efficiency and effectiveness of an organization.
								</p>
							)
						},
						{
							title: `Why Custom Software Development Is Important?`,
							content: (
								<p>
									All companies have access to the same off-the-shelf solutions.
									While these packages can provide a strong foundation for a
									company's software ecosystem, they are not optimized to address
									each organization's unique needs. Custom software provides the
									"glue" that binds pre-packaged solutions together in a way that
									maximizes ROI by offering solutions that automate many aspects
									of a company's business processes.
								</p>
							)
						},
						{
							title: 'How Much Does Custom Software Development Cost?',
							content: (
								<p>
									Due to each solution's unique nature, custom software is often
									the most expensive way to bring new software into a company.
									These costs can be dramatically reduced by limiting the scope of
									custom development projects to provide only those features that
									are not available from off-the-shelf solutions. These reductions
									are typically realized by integrating existing solutions into
									the custom software project. Additional cost benefits can be
									gained by partnering with a company, like SitePen, with
									extensive experience with this kind of development.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default CustomSoftwareDevelopment;
